/*
  Keeps the option dropdowns on a combination that actually exists.

  Size and Flavor are chosen independently, so a shopper can ask for a pair that was never made —
  909g together with a flavour only ever bottled in 2.27kg. Shopify answers that with no variant at
  all, which leaves the page with no price, no add to cart, and no back-in-stock form either: a
  stock notification is stored against a variant id, and there is none to store it against. So
  instead of leaving that dead end on screen, the option the shopper did NOT just touch is moved to
  a value that exists alongside the one they did, preferring a value that is in stock.

  Two details worth keeping in mind when editing:

  - The listener is on document, not on <variant-selects>: product-info.js replaces that element
    wholesale after every variant change, and a listener bound to it would go with it.
  - It runs in the capture phase so the selection is settled before VariantSelects reads the selects
    and publishes. Bound in the bubble phase it would fire after the fetch for the dead pair had
    already gone out, and the section would be requested twice.
*/
(() => {
  const readVariantMap = (variantSelects) => {
    const node = variantSelects.querySelector('script[data-variant-map]');

    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch {
      // A malformed map must not take the picker down with it — without one the guard just idles.
      return null;
    }
  };

  /*
    VariantSelects reads `option[selected]`, the attribute, not the property, so moving the
    attribute is what actually changes the published selection. The custom dropdown is rebuilt
    afterwards so its trigger text and its checkmark follow.
  */
  const applyValue = (select, value) => {
    const option = Array.from(select.options).find((candidate) => candidate.value === value);

    if (!option || option.hasAttribute('selected')) return;

    select.querySelectorAll('option[selected]').forEach((selected) => selected.removeAttribute('selected'));
    select.value = value;
    option.setAttribute('selected', 'selected');

    const dropdown = select.closest('pi-variant-dropdown');

    if (dropdown && typeof dropdown.buildList === 'function') dropdown.buildList();
  };

  document.addEventListener(
    'change',
    (event) => {
      const select = event.target;

      if (!(select instanceof HTMLSelectElement)) return;

      const variantSelects = select.closest('variant-selects');

      if (!variantSelects) return;

      // One option cannot contradict anything, so there is nothing to reconcile.
      const selects = Array.from(variantSelects.querySelectorAll('select'));

      if (selects.length < 2) return;

      const variants = readVariantMap(variantSelects);

      if (!variants?.length) return;

      const chosen = selects.map(({ value }) => value);
      const combinationExists = variants.some((variant) =>
        chosen.every((value, index) => variant.options[index] === value)
      );

      if (combinationExists) return;

      const changedIndex = selects.indexOf(select);

      if (changedIndex === -1) return;

      // Whatever the shopper just picked is the fixed point; everything else gives way to it.
      const candidates = variants.filter((variant) => variant.options[changedIndex] === chosen[changedIndex]);

      if (!candidates.length) return;

      const resolved = candidates.find(({ available }) => available) || candidates[0];

      selects.forEach((other, index) => {
        if (index !== changedIndex) applyValue(other, resolved.options[index]);
      });
    },
    true
  );
})();
